'use client';

import { Suspense, useEffect, useState } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { ArrowLeft } from 'lucide-react';

import { useTranslation } from '@/hooks/useTranslation';
import { colors, typography } from '@/styles/theme';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { useFieldDefs, getFieldLabel, isSkeletonField } from '../_lib/useFieldDefs';
import { robotApi, type RobotStatus, type RobotUnit } from '../_lib/api';
import { formatMoney } from '../_lib/formatMoney';

const STATUS_COLORS: Record<RobotStatus, { bg: string; text: string }> = {
  ORDERED: { bg: '#e8f3ff', text: '#3370ff' },
  IN_TRANSIT: { bg: '#fff7e8', text: '#ff7d00' },
  BONDED: { bg: '#f0e8ff', text: '#7b61ff' },
  IN_STOCK: { bg: '#e8ffea', text: '#00b42a' },
  RESERVED: { bg: '#fff8e6', text: '#d4a017' },
  SOLD: { bg: '#e0f7f2', text: '#00a870' },
  DELIVERED: { bg: '#e6fff0', text: '#0fc66e' },
  REPAIR: { bg: '#ffece8', text: '#f53f3f' },
  REPAIRED: { bg: '#fff0e6', text: '#e8730a' },
  CANCELLED: { bg: '#f7f8fa', text: '#8f959e' },
};

function formatValue(v: any, type: string, locale: string, currency?: string | null): string {
  if (v == null || v === '') return '-';
  if (type === 'money') {
    const n = typeof v === 'number' ? v : Number(v);
    if (!Number.isFinite(n)) return String(v);
    return formatMoney(n, currency ?? null);
  }
  if (type === 'date' || type === 'datetime') {
    return new Date(v).toLocaleDateString(locale === 'en' ? 'en-US' : 'zh-CN');
  }
  if (type === 'bool') return v === true ? '✓' : v === false ? '✗' : '-';
  return String(v);
}

function CompareInner() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const { t, locale } = useTranslation();
  const rm = t.robotManager;

  const ids = (searchParams.get('ids') || '').split(',').filter(Boolean);
  const { fieldDefs, loading: defsLoading } = useFieldDefs();

  const [units, setUnits] = useState<RobotUnit[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ids.length === 0) {
      setLoading(false);
      return;
    }
    Promise.all(ids.map((id) => robotApi.get(id).catch(() => null)))
      .then((results) => setUnits(results.filter((r): r is RobotUnit => !!r)))
      .finally(() => setLoading(false));
  }, [searchParams]);

  if (loading || defsLoading) {
    return (
      <div className="h-full flex flex-col">
        <div className="h-12 border-b" style={{ borderColor: colors.border }} />
        <div className="p-8">
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  if (units.length === 0) {
    return (
      <div className="h-full flex flex-col items-center justify-center gap-3" style={{ color: colors.textTertiary }}>
        <p>{rm.compare.noUnits}</p>
        <Button variant="outline" size="sm" onClick={() => router.push('/robot-manager')}>
          {rm.actions.back}
        </Button>
      </div>
    );
  }

  const hasDiff = (values: any[]): boolean => {
    const set = new Set(values.map((v) => JSON.stringify(v ?? null)));
    return set.size > 1;
  };

  // 组装对比行：[{ label, values, group }]
  type Row = { label: string; key: string; type: string; values: any[]; group: string };
  const rows: Row[] = [];

  // 骨架字段
  rows.push({
    label: rm.fields.currentStatus,
    key: 'currentStatus',
    type: 'status',
    values: units.map((u) => u.currentStatus),
    group: 'skeleton',
  });
  rows.push({
    label: rm.fields.model,
    key: 'modelId',
    type: 'text',
    values: units.map((u) => u.model?.name ?? '-'),
    group: 'skeleton',
  });
  rows.push({
    label: rm.fields.sku,
    key: 'skuId',
    type: 'text',
    values: units.map((u) => u.sku?.code ?? '-'),
    group: 'skeleton',
  });
  rows.push({
    label: rm.purchase.supplier,
    key: 'supplierId',
    type: 'text',
    values: units.map((u) => u.supplier?.name ?? '-'),
    group: 'skeleton',
  });
  rows.push({
    label: rm.sales.customerName,
    key: 'customerId',
    type: 'text',
    values: units.map((u) => u.customer?.name ?? '-'),
    group: 'skeleton',
  });
  rows.push({
    label: rm.fields.location,
    key: 'locationId',
    type: 'text',
    values: units.map((u) => u.location?.name ?? '-'),
    group: 'skeleton',
  });

  // 动态字段
  const dynamicDefs = fieldDefs.filter((d) => !isSkeletonField(d.key));
  for (const def of dynamicDefs) {
    rows.push({
      label: getFieldLabel(def, locale),
      key: def.key,
      type: def.type,
      values: units.map((u) => (u.metadata as any)?.[def.key]),
      group: def.group,
    });
  }

  return (
    <div className="h-full flex flex-col" style={{ backgroundColor: colors.bgSecondary }}>
      <div className="h-12 flex items-center justify-between px-6 bg-white border-b" style={{ borderColor: colors.border }}>
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => router.push('/robot-manager')}>
            <ArrowLeft className="w-4 h-4" />
          </Button>
          <h1 className={typography.h4}>{rm.compare.title}</h1>
          <span className={typography.caption}>
            {rm.compare.unitsCount.replace('{count}', String(units.length))}
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="max-w-full p-6">
          <div
            className="rounded-lg overflow-auto bg-white"
            style={{ border: `1px solid ${colors.border}` }}
          >
            <table className="w-full text-sm tabular-nums">
              <thead>
                <tr style={{ backgroundColor: '#f7f8fa', borderBottom: `1px solid ${colors.border}` }}>
                  <th
                    className="px-3 py-3 text-left font-semibold sticky left-0 z-10"
                    style={{ color: colors.textSecondary, backgroundColor: '#f7f8fa', minWidth: 160 }}
                  >
                    {rm.compare.field}
                  </th>
                  {units.map((u) => (
                    <th
                      key={u.id}
                      className="px-3 py-3 text-left font-semibold"
                      style={{ color: colors.textSecondary, minWidth: 200 }}
                    >
                      <div className="flex flex-col gap-1">
                        <a
                          href={`/robot-manager/${u.id}`}
                          className={typography.link}
                        >
                          {u.ffsn}
                        </a>
                        <Badge
                          className="self-start"
                          style={{
                            backgroundColor: STATUS_COLORS[u.currentStatus].bg,
                            color: STATUS_COLORS[u.currentStatus].text,
                          }}
                        >
                          {rm.status[u.currentStatus]}
                        </Badge>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => {
                  const diff = hasDiff(row.values);
                  return (
                    <tr
                      key={row.key}
                      style={{
                        borderBottom: `1px solid ${colors.border}`,
                        backgroundColor: diff ? '#fff9e8' : undefined,
                      }}
                    >
                      <td
                        className="px-3 py-2.5 font-medium sticky left-0 z-10"
                        style={{
                          color: colors.textSecondary,
                          backgroundColor: diff ? '#fff9e8' : colors.bgPrimary,
                        }}
                      >
                        {row.label}
                      </td>
                      {row.values.map((v, i) => (
                        <td key={i} className="px-3 py-2.5" style={{ color: colors.textPrimary }}>
                          {row.type === 'status' ? (
                            <Badge
                              style={{
                                backgroundColor: STATUS_COLORS[v as RobotStatus]?.bg,
                                color: STATUS_COLORS[v as RobotStatus]?.text,
                              }}
                            >
                              {rm.status[v as RobotStatus]}
                            </Badge>
                          ) : (
                            formatValue(v, row.type, locale, (units[i].metadata as any)?.currency)
                          )}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className={`${typography.caption} mt-3`}>
            <span
              className="inline-block w-3 h-3 rounded-sm align-middle mr-1"
              style={{ backgroundColor: '#fff9e8', border: `1px solid ${colors.border}` }}
            />
            {rm.compare.diffHint}
          </p>
        </div>
      </div>
    </div>
  );
}

export default function ComparePage() {
  return (
    <Suspense fallback={<div />}>
      <CompareInner />
    </Suspense>
  );
}
